import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import shippingOptions from '../../data/shippingOptions.json';
import { decimalCents, parseCents, usd } from './cashMoney';
import {
  affordablePieces,
  categoryLabel,
  CREDIT_CATEGORIES,
  validCalculatorSettings,
} from './bankCalculatorContract';
import { validBalance } from './cashContract';
import './CashBank.css';
import CategoryPricingCard from './CategoryPricingCard';

// A read-only estimate is available independently of funding and ordering activation.
export default function BankCalculator({ balance: suppliedBalance, distributorId }) {
  const t = useT();
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [familyKey, setFamilyKey] = useState('');
  const [speed, setSpeed] = useState('');
  const [budget, setBudget] = useState(null);
  const calculator = useRef(null);
  const { begin, version } = useShippingRequest(`calculator:${distributorId || 'own'}`);
  useEffect(() => {
    const request = begin();
    setDetails(null);
    setError(false);
    setBudget(null);
    setFamilyKey('');
    setSpeed('');
    Promise.all([
      suppliedBalance
        ? Promise.resolve({ data: { data: suppliedBalance } })
        : distributorService.cashBalance(distributorId, { signal: request.signal }),
      distributorService.cashSettings(distributorId, { signal: request.signal }),
    ])
      .then(([money, configuration]) => {
        if (!request.isCurrent()) return;
        const balance = money?.data?.data;
        const settings = configuration?.data?.data;
        if (
          !validBalance(balance) ||
          !validCalculatorSettings(settings) ||
          (distributorId && settings.distributorId !== distributorId) ||
          (balance.distributorId && balance.distributorId !== settings.distributorId)
        )
          throw new Error('Invalid calculator details');
        setDetails({
          balance,
          settings,
          categoryRates: settings.pricingBasis === 'piece' ? settings.categoryRates : [],
        });
      })
      .catch(() => {
        if (request.isCurrent()) setError(true);
      });
  }, [begin, version, distributorId, suppliedBalance, retry]);
  const ready = Boolean(details) || error;
  useEffect(() => {
    if (ready && window.location.hash === '#buying-power-calculator') {
      calculator.current?.scrollIntoView?.({ block: 'start' });
      calculator.current?.focus({ preventScroll: true });
    }
  }, [ready]);
  const { balance, settings, categoryRates = [] } = details || {};
  const amount = budget ?? (balance ? decimalCents(balance.balanceCents) : '');
  const cents = parseCents(amount);
  const rate = categoryRates.find((row) => row.familyKey === familyKey);
  const shipping = settings?.shippingRanges.find((row) => row.shippingSpeed === speed);
  const quantity =
    rate && shipping && cents !== null && !balance.spendingBlocked
      ? affordablePieces(cents, rate.pricePerPieceCents, shipping.maxCents)
      : null;
  return (
    <div className="me-cash">
      {details && <CategoryPricingCard settings={settings} t={t} />}
      <fieldset
        className="me-cash-card me-cash-calculator"
        id="buying-power-calculator"
        tabIndex={-1}
        ref={calculator}
      >
        <legend>{t('Bank calculator')}</legend>
        <p className="me-cash-heading">
          {t('See how many physical pieces your dollar balance can buy, including shipping.')}
        </p>
        {error ? (
          <p role="status">
            {t('Calculator pricing and balance are unavailable. Try again.')}{' '}
            <button type="button" onClick={() => setRetry((value) => value + 1)}>
              {t('Retry calculator')}
            </button>
          </p>
        ) : !details ? (
          <p role="status">{t('Loading calculator…')}</p>
        ) : (
          <>
            <p>{t('Current dollar balance: {amount}', { amount: usd(balance.balanceCents) })}</p>
            <div className="me-cash-grid">
              <label>
                {t('Amount to estimate ($)')}
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setBudget(event.target.value)}
                />
              </label>
              <label>
                {t('Product category')}
                <select value={familyKey} onChange={(event) => setFamilyKey(event.target.value)}>
                  <option value="">{t('Choose category')}</option>
                  {CREDIT_CATEGORIES.map((key) => (
                    <option key={key} value={key}>
                      {categoryLabel(key)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t('Shipping service')}
                <select value={speed} onChange={(event) => setSpeed(event.target.value)}>
                  <option value="">{t('Choose shipping service')}</option>
                  {settings.shippingRanges
                    .filter((row) => shippingOptions.includes(row.shippingSpeed))
                    .map((row) => (
                      <option key={row.shippingSpeed} value={row.shippingSpeed}>
                        {t(row.shippingSpeed)}
                      </option>
                    ))}
                </select>
              </label>
            </div>
            {cents === null && (
              <p role="status">{t('Enter a valid dollar amount with up to two decimal places.')}</p>
            )}
            {familyKey && !rate && (
              <p role="status">{t('Pricing is not configured for this category.')}</p>
            )}
            {categoryRates.length === 0 && !familyKey && (
              <p role="status">
                {t('Category pricing has not been configured. Contact MedEffects.')}
              </p>
            )}
            {settings.shippingRanges.length === 0 && (
              <p role="status">
                {t('Shipping pricing has not been configured. Contact MedEffects.')}
              </p>
            )}
            {balance.spendingBlocked && (
              <p role="status">{t('Spending is paused. Buying power is unavailable.')}</p>
            )}
            {rate && (
              <p>
                {t('MedEffects category price: {amount} per piece', {
                  amount: usd(rate.pricePerPieceCents),
                })}
              </p>
            )}
            {familyKey && (
              <p>
                {t(
                  'The category price is the same for every size. One physical piece counts as one unit.'
                )}
              </p>
            )}
            {quantity !== null && (
              <div className="me-cash-result" role="status">
                <b>{quantity.toLocaleString('en-US')}</b>{' '}
                {t(quantity === 1 ? 'physical piece available' : 'physical pieces available')}
                <p>
                  {t(
                    'Allows up to {amount} for shipping. This estimate does not reserve funds; the order quote confirms the total.',
                    { amount: usd(shipping.maxCents) }
                  )}
                </p>
              </div>
            )}
            {cents !== null && cents > balance.balanceCents && (
              <p>{t('This amount exceeds your current bank balance.')}</p>
            )}
            {budget !== null && (
              <button type="button" onClick={() => setBudget(null)}>
                {t('Use current bank balance')}
              </button>
            )}
            {!balance.enabled && (
              <p>{t('Estimate only. Cash ordering is not active for this account.')}</p>
            )}
          </>
        )}
      </fieldset>
    </div>
  );
}
